import React, { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from '@2pulse/responsive';
import { DaysSelect, getDaysFR, Days } from '@app/components/form/selects/features/DaysSelect';
import { DateHourPicker } from '@app/components/form/DateTimeAndHourPicker';
import { Button, ButtonIcon, SvgBin } from '@app/components';
import { TimeSlotInput } from '@app/components/form/TimeSlotInput';
import { col, row, worksansoxford } from '@app/style';
import { normalize } from '@app/helpers';
import { isDefined } from '@app/helpers/kindOf';

const convertTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

const convertMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

interface ScheduleFormProps {
  label: string;
  aide?: React.ReactNode;
  names: {
    schedule: string;
    selectedDays: string;
    startTime: string;
    endTime: string;
  };
}

export const ScheduleForm = ({ label, aide, names }: ScheduleFormProps) => {
  const { watch, setValue, getValues, setError, clearErrors, formState: { errors } } = useFormContext();

  const [schedule = {}, selectedDays, startTime, endTime] = watch([
    names.schedule,
    names.selectedDays,
    names.startTime,
    names.endTime
  ]);

  const addTimeSlot = () => {
    if (selectedDays && selectedDays.length > 0 && startTime && endTime) {
      const startTimeInMinutes = convertTimeToMinutes(startTime);
      const endTimeInMinutes = convertTimeToMinutes(endTime);

      if (startTimeInMinutes >= endTimeInMinutes) {
        setError('endTime', { message: "L'heure de fin doit être supérieure à l'heure de début" });
        return;
      }

      clearErrors();

      // Ajouter l'horaire à tous les jours sélectionnés
      selectedDays.forEach((day: Days) => {
        const currentTimes = getValues(`${names.schedule}.${day}`) || [];
        const newTimes = [...currentTimes, [startTimeInMinutes, endTimeInMinutes]];
        setValue(`${names.schedule}.${day}`, newTimes);
      });

      setValue(names.startTime, '');
      setValue(names.endTime, '');
    }
  };

  const removeTimeSlot = (day: Days, index: number) => {
    const currentTimes = getValues(`${names.schedule}.${day}`) || [];
    const updatedTimes = currentTimes.filter((_, i) => i !== index);
    setValue(`${names.schedule}.${day}`, updatedTimes);
  }

  const hasAnySchedule = Object.values(Days).some((day) => 
    isDefined(schedule) && schedule[day] && schedule[day].length > 0
  );

  return (
    <Fragment>

      <AvailabilitySection>

        <DaysSelect
          name={names.selectedDays}
          label='Sélectionner les jours'
          isMultiple />

        {selectedDays && selectedDays.length > 0 && (
          <TimeSlotPickerSection>
            <TimeSlotPickerContainer>
              <DateHourPicker
                name={names.startTime}
                label="Heure de début"
              />
              <DateHourPicker
                name={names.endTime}
                label="Heure de fin"
              />
            </TimeSlotPickerContainer>

            <Button onPress={addTimeSlot} title="Appliquer cet horaire aux jours sélectionnés" />
          </TimeSlotPickerSection>
        )}

        <TimeSlotFormContainer>
          <TimeSlotFormHeader>
            <TimeSlotFormLabel>{label}</TimeSlotFormLabel>
            {aide}
          </TimeSlotFormHeader>
          <TimeSlotFormContent>
            {!hasAnySchedule ? (
              <NoScheduleMessage>Aucun horaire défini</NoScheduleMessage>
            ) : (
              Object.values(Days)
                .filter((day) => isDefined(schedule) && schedule[day] && schedule[day].length > 0)
                .map((day) => (
                  <TimeSlotsFormRow key={day}>
                    <TimeSlotLabel>{getDaysFR(day)}</TimeSlotLabel>
                    <TimeSlotsRow
                      contentContainerStyle={{
                        flexDirection: 'row',
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 8
                      }}
                      showsHorizontalScrollIndicator={false}
                      horizontal
                    >
                      {schedule[day]?.map((slot: [number, number], index: number) => {
                        const slotError = errors?.[names.schedule]?.[day]?.[index];

                        return (
                          <TimeSlotRow key={index}>
                            <TimeSlotInput
                              size="small"
                              keyboardType='numeric'
                              showError={!!slotError}
                              name={`${names.schedule}.${day}`}
                              day={day}
                              index={index}
                              value={`${convertMinutesToTime(slot[0])} - ${convertMinutesToTime(slot[1])}`}
                            />

                            <TimeSlotDelete>
                              <ButtonIcon nude onPress={() => removeTimeSlot(day, index)} Icon={SvgBin} />
                            </TimeSlotDelete>
                          </TimeSlotRow>
                        )
                      })}
                    </TimeSlotsRow>
                  </TimeSlotsFormRow>
                ))
            )}
          </TimeSlotFormContent>
        </TimeSlotFormContainer>
      </AvailabilitySection>

    </Fragment>
  );
};

const AvailabilitySection = styled.View({
  marginTop: 21,
  // paddingBottom: 20,
});

const TimeSlotPickerContainer = styled.View({
  ...row,
  alignItems: "center",
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 13,
});

const TimeSlotsFormRow = styled.View(({ theme }) => ({
  ...row,
  overflow: 'hidden',
  alignItems: 'center',
  borderTopWidth: 1,
  borderTopColor: theme.colors.translucid,
  height: normalize(76)
}));

const TimeSlotLabel = styled.Text(({ theme }) => ({
  ...worksansoxford,
  // textAlign: 'center',
  fontWeight: '400',
  fontSize: normalize(21),
  color: theme.colors.babypowder,
  width: 120,
  flexShrink: 0
}));

const TimeSlotFormHeader = styled.View({
  ...row,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  // La bulle d'aide (`aide`) déborde sous l'en-tête : sans zIndex, la liste
  // des jours rendue après la recouvrirait.
  zIndex: 10,
});

const TimeSlotFormLabel = styled.Text(({ theme }) => ({
  ...worksansoxford,
  fontWeight: '700',
  fontSize: normalize(21),
  paddingTop: normalize(34),
  paddingBottom: normalize(21),
  textAlign: 'center',
  color: theme.colors.babypowder,
}));

const TimeSlotPickerSection = styled.View({
  ...col,
  gap: 21,
  marginTop: 21,
});

const NoScheduleMessage = styled.Text(({ theme }) => ({
  ...worksansoxford,
  fontWeight: '400',
  fontSize: normalize(18),
  color: theme.colors.babypowder,
  textAlign: 'center',
  paddingVertical: normalize(20),
  opacity: 0.7,
}));

const TimeSlotRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: 190,
  overflow: 'hidden',
});

const TimeSlotsRow = styled.ScrollView({});

const TimeSlotFormContainer = styled.View(({ theme }) => ({
  ...col,
  minHeight: 76,
  backgroundColor: 'rgba(240, 249 , 252, .04)',
  marginTop: 21
}));

const TimeSlotFormContent = styled.View(({ theme }) => ({
  padding: 20,
}));

const TimeSlotDelete = styled.View(({ theme }) => ({
  position: 'absolute',
  top: 1,
  right: 5
}));